// =============================================================================
// userPreferences — one central home for "things Mantle remembers per user".
//
// Backed by localStorage, namespaced per user (uid) so two people sharing a
// browser profile don't inherit each other's layout. Use this for low-stakes
// UI preferences (which columns/stages are shown, expand/collapse state, etc.).
//
// TO ADD A NEW REMEMBERED SELECTION:
//   1. Add a key to the PREFS registry below.
//   2. Seed your state from userPrefs.get(PREFS.YOUR_KEY, fallback) and
//      persist it with userPrefs.set(PREFS.YOUR_KEY, value) (usually in a
//      small useEffect). That's it.
//
// NOTE: Role / Site / Version are deliberately NOT here. They persist via
// AppContext under the fixed keys roleUrid / siteCode / budgetVersionId, which
// the axios interceptor (services/api.js) reads to build request headers.
// Moving them would break that header injection.
// =============================================================================

const NS = 'mantle:pref';

// The single source of truth for what we remember. Add/remove entries here.
export const PREFS = {
  STOCKED_HIDDEN_STAGES: 'stocked.hiddenStages',   // { [stageCode]: true } -> hidden
  STOCKED_STAGE_COLLAPSED: 'stocked.stageCollapsed', // { [stageCode]: true } -> collapsed
  STOCKED_PERIODS: 'stocked.periods',   // [1,2,…] — months Stocked Pricing writes to
  // Shared by BOTH Purchase pricing pages, so moving between them keeps your
  // place. Warehouse is keyed BY SITE ({ [siteCode]: warehouseId }) — a warehouse
  // id only means anything inside its own site.
  PURCHASE_WAREHOUSE: 'purchase.warehouse',
  PURCHASE_PRODUCT_CLASS: 'purchase.productClass', // '' = All classes
  BOM_SCOPE: 'bom.scope', // 'FG' | 'WIP' | 'ALL' — item scope on the BOM page
};

// Remembered selections are only ever a convenience: anything restored from here
// must still be checked against what the server actually returned (a warehouse
// can be deleted, a product class can disappear) before it is used.

const currentUid = () => localStorage.getItem('uid') || 'anon';
const fullKey = (key) => `${NS}:${currentUid()}:${key}`;

export const userPrefs = {
  // Read a stored preference, or `fallback` if it was never set / unreadable.
  get(key, fallback = null) {
    try {
      const raw = localStorage.getItem(fullKey(key));
      return raw === null ? fallback : JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  // Persist a preference (any JSON-serialisable value).
  set(key, value) {
    try {
      localStorage.setItem(fullKey(key), JSON.stringify(value));
    } catch {
      /* storage full or disabled — preferences are best-effort, so ignore */
    }
  },

  remove(key) {
    localStorage.removeItem(fullKey(key));
  },

  // Wipe every remembered preference for the current user (e.g. a
  // "Reset my preferences" button). Does not touch role/site/version/auth.
  clearAll() {
    const prefix = `${NS}:${currentUid()}:`;
    Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => localStorage.removeItem(k));
  },
};

export default userPrefs;
